import { useCallback, useEffect, useRef, useState } from 'react';
import api, { getCrmChatAssetUrl } from '../utils/api';
import { useToast } from './use-toast';
import { formatChatTime, formatDate } from '../utils/appDateTime';
import { compressImageForChat, isImageChatFile } from '../utils/crmChatImageCompress';

const UNREAD_POLL_MS = 30000;
const EMPLOYEES_POLL_MS = 15000;
const MESSAGES_POLL_MS = 5000;
const SOUND_STORAGE_KEY = 'crm_chat_sound_enabled';

function isSoundEnabled() {
  try {
    const v = localStorage.getItem(SOUND_STORAGE_KEY);
    return v === null || v === 'true';
  } catch {
    return true;
  }
}

function playNotificationSound() {
  if (!isSoundEnabled()) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 800;
    gain.gain.value = 0.08;
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch {
    // ignore
  }
}

export function formatLastSeen(iso) {
  if (!iso) return 'Offline';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Offline';
  const diff = Date.now() - d.getTime();
  if (diff < 120000) return 'Online';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Last seen ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `Last seen ${hrs}h ago`;
  return `Last seen ${formatDate(d, { month: 'short', day: 'numeric' })}`;
}

export { formatChatTime, SOUND_STORAGE_KEY, isSoundEnabled };

export function buildMessagePreview(message) {
  if (!message) return '';
  const body = (message.body || '').trim();
  if (body) return body.length > 120 ? `${body.slice(0, 117)}...` : body;
  if (message.message_type === 'image') return 'Photo';
  return message.attachment_original_name || 'Document';
}

export function buildReplyTarget(message) {
  if (!message?.id) return null;
  return {
    id: message.id,
    sender_name: message.is_mine ? 'You' : (message.sender_name || 'User'),
    body: message.body || '',
    body_preview: buildMessagePreview(message),
    message_type: message.message_type || 'text',
  };
}

function mergeMessagesById(prev, incoming) {
  if (!incoming.length) return prev;
  if (!prev.length) return incoming;

  const byId = new Map(prev.map((m) => [m.id, m]));
  for (const m of incoming) {
    byId.set(m.id, m);
  }
  return [...byId.values()].sort((a, b) => a.id - b.id);
}

function countNewIncoming(prev, incoming) {
  if (!prev.length || !incoming.length) return 0;
  const newestPrev = prev[prev.length - 1].id;
  return incoming.filter((m) => m.id > newestPrev && !m.is_mine).length;
}

export default function useCrmChat({ enabled, panelOpen, activeUserId }) {
  const { toast } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [activeContact, setActiveContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [pendingNewCount, setPendingNewCount] = useState(0);
  const lastMessageIdsRef = useRef({});
  const prevUnreadRef = useRef(0);
  const messageRefsRef = useRef({});
  const listContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const userJustSentRef = useRef(false);
  const hasMoreMessagesRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const messagesRef = useRef([]);
  const activeContactRef = useRef(null);
  const scrollingToMessageRef = useRef(false);

  useEffect(() => {
    hasMoreMessagesRef.current = hasMoreMessages;
  }, [hasMoreMessages]);

  useEffect(() => {
    loadingOlderRef.current = loadingOlder;
  }, [loadingOlder]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    activeContactRef.current = activeContact;
  }, [activeContact]);

  const registerMessageRef = useCallback((id, el) => {
    if (el) {
      messageRefsRef.current[id] = el;
    } else {
      delete messageRefsRef.current[id];
    }
  }, []);

  const cancelReply = useCallback(() => setReplyTo(null), []);

  const setReplyTarget = useCallback((message) => {
    setReplyTo(buildReplyTarget(message));
  }, []);

  const setNearBottom = useCallback((near) => {
    isNearBottomRef.current = near;
    if (near) {
      setPendingNewCount(0);
    }
  }, []);

  const clearPendingNew = useCallback(() => {
    setPendingNewCount(0);
  }, []);

  const consumeJustSent = useCallback(() => {
    const was = userJustSentRef.current;
    userJustSentRef.current = false;
    return was;
  }, []);

  const highlightMessage = useCallback((messageId) => {
    const el = messageRefsRef.current[messageId];
    if (!el) return false;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
    setTimeout(() => setHighlightedMessageId(null), 2000);
    return true;
  }, []);

  const scrollToMessage = useCallback(async (messageId) => {
    if (!messageId || scrollingToMessageRef.current) return;
    if (highlightMessage(messageId)) return;

    scrollingToMessageRef.current = true;
    try {
      let attempts = 0;
      while (attempts < 20) {
        if (messageRefsRef.current[messageId]) {
          highlightMessage(messageId);
          return;
        }
        if (!hasMoreMessagesRef.current || loadingOlderRef.current) break;

        const conversationId = activeContactRef.current?.conversation_id;
        const firstId = messagesRef.current[0]?.id;
        if (!conversationId || !firstId) break;

        loadingOlderRef.current = true;
        setLoadingOlder(true);
        try {
          const params = { limit: 50, before_id: firstId };
          const res = await api.get(`/crm/chat/conversations/${conversationId}/messages`, { params });
          const incoming = res.data?.data?.messages || [];
          const hasMore = res.data?.data?.has_more ?? false;
          setHasMoreMessages(hasMore);
          hasMoreMessagesRef.current = hasMore;
          if (!incoming.length) break;
          setMessages((prev) => [...incoming, ...prev]);
          // Wait for React to commit refs
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
        } catch {
          break;
        } finally {
          loadingOlderRef.current = false;
          setLoadingOlder(false);
        }
        attempts += 1;
      }
      highlightMessage(messageId);
    } finally {
      scrollingToMessageRef.current = false;
    }
  }, [highlightMessage]);

  const fetchUnread = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await api.get('/crm/chat/unread-count');
      const total = res.data?.data?.unread_total ?? 0;
      setUnreadTotal(total);
      prevUnreadRef.current = total;
    } catch {
      // ignore
    }
  }, [enabled]);

  const sendHeartbeat = useCallback(async () => {
    if (!enabled) return;
    try {
      await api.post('/crm/chat/presence/heartbeat');
    } catch {
      // ignore
    }
  }, [enabled]);

  const fetchEmployees = useCallback(async (q = searchQuery) => {
    if (!enabled || !panelOpen) return;
    setLoadingEmployees(true);
    try {
      const res = await api.get('/crm/chat/employees', { params: q ? { q } : {} });
      setEmployees(res.data?.data?.items || []);
    } catch {
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  }, [enabled, panelOpen, searchQuery]);

  const fetchMessages = useCallback(async (conversationId, options = {}) => {
    if (!conversationId) return;
    const { beforeId, append = false } = options;
    if (beforeId) {
      setLoadingOlder(true);
      loadingOlderRef.current = true;
    } else if (!append) {
      setLoadingMessages(true);
    }
    try {
      const params = { limit: 50 };
      if (beforeId) params.before_id = beforeId;
      const res = await api.get(`/crm/chat/conversations/${conversationId}/messages`, { params });
      const incoming = res.data?.data?.messages || [];
      const hasMore = res.data?.data?.has_more ?? false;
      setHasMoreMessages(hasMore);
      hasMoreMessagesRef.current = hasMore;

      if (append) {
        setMessages((prev) => [...incoming, ...prev]);
      } else {
        setMessages(incoming);
        setPendingNewCount(0);
        isNearBottomRef.current = true;
        const last = incoming[incoming.length - 1];
        if (last?.id) {
          lastMessageIdsRef.current[conversationId] = last.id;
        }
      }
    } catch {
      if (!append) setMessages([]);
    } finally {
      setLoadingMessages(false);
      setLoadingOlder(false);
      loadingOlderRef.current = false;
    }
  }, []);

  const markRead = useCallback(async (conversationId) => {
    if (!conversationId) return;
    try {
      await api.post(`/crm/chat/conversations/${conversationId}/read`);
      setEmployees((prev) =>
        prev.map((e) =>
          e.conversation_id === conversationId ? { ...e, unread_count: 0 } : e
        )
      );
      await fetchUnread();
    } catch {
      // ignore
    }
  }, [fetchUnread]);

  const openConversationWith = useCallback(async (contact) => {
    if (!contact?.user_id) return null;
    setActiveContact(contact);
    setReplyTo(null);
    setPendingNewCount(0);
    try {
      const res = await api.post('/crm/chat/conversations/direct', {
        recipient_user_id: contact.user_id,
      });
      const conv = res.data?.data?.conversation;
      const other = res.data?.data?.other_user || contact;
      const conversationId = conv?.id;
      if (conversationId) {
        setActiveContact({ ...contact, ...other, conversation_id: conversationId });
        await fetchMessages(conversationId);
        await markRead(conversationId);
        await fetchEmployees();
      }
      return conversationId;
    } catch {
      return null;
    }
  }, [fetchMessages, markRead, fetchEmployees]);

  const appendSentMessage = useCallback((conversationId, newMsg) => {
    if (newMsg) {
      userJustSentRef.current = true;
      setMessages((prev) => [...prev, newMsg]);
      lastMessageIdsRef.current[conversationId] = newMsg.id;
      setPendingNewCount(0);
    }
  }, []);

  const sendMessage = useCallback(async (text, options = {}) => {
    const conversationId = activeContact?.conversation_id;
    if (!conversationId || !text.trim()) return false;
    const body = text.trim();
    const replyToId = options.replyToId ?? replyTo?.id ?? null;
    try {
      const res = await api.post(`/crm/chat/conversations/${conversationId}/messages`, {
        message: body,
        ...(replyToId ? { reply_to_message_id: replyToId } : {}),
      });
      const newMsg = res.data?.data;
      if (newMsg) {
        appendSentMessage(conversationId, newMsg);
      } else {
        await fetchMessages(conversationId);
      }
      setReplyTo(null);
      await fetchEmployees();
      return true;
    } catch (err) {
      toast({
        title: 'Send failed',
        description: err.response?.data?.message || 'Could not send message.',
        variant: 'destructive',
      });
      return false;
    }
  }, [activeContact, replyTo, appendSentMessage, fetchMessages, fetchEmployees, toast]);

  const sendAttachment = useCallback(async (file, options = {}) => {
    const conversationId = activeContact?.conversation_id;
    if (!conversationId || !file) return false;

    const replyToId = options.replyToId ?? replyTo?.id ?? null;
    const caption = options.caption ?? '';
    const capturedAt = options.capturedAt ?? null;

    let uploadFile = file;
    if (isImageChatFile(file)) {
      try {
        uploadFile = await compressImageForChat(file);
      } catch (err) {
        toast({
          title: 'Image too large',
          description: err.message || 'Could not compress image.',
          variant: 'destructive',
        });
        return false;
      }
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (caption.trim()) formData.append('message', caption.trim());
    if (replyToId) formData.append('reply_to_message_id', String(replyToId));
    if (capturedAt) formData.append('captured_at', capturedAt);

    setUploadProgress(1);
    try {
      const res = await api.post(
        `/crm/chat/conversations/${conversationId}/messages`,
        formData,
        {
          onUploadProgress: (evt) => {
            if (evt.total) {
              setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
            }
          },
        }
      );
      const newMsg = res.data?.data;
      if (newMsg) {
        appendSentMessage(conversationId, newMsg);
      } else {
        await fetchMessages(conversationId);
      }
      setReplyTo(null);
      await fetchEmployees();
      return true;
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.message || 'Could not send attachment.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setUploadProgress(0);
    }
  }, [activeContact, replyTo, appendSentMessage, fetchMessages, fetchEmployees, toast]);

  const loadOlderMessages = useCallback(async () => {
    const conversationId = activeContact?.conversation_id;
    if (!conversationId || !messages.length || loadingOlder || !hasMoreMessages) return;
    const firstId = messages[0]?.id;
    if (!firstId) return;
    await fetchMessages(conversationId, { beforeId: firstId, append: true });
  }, [activeContact, messages, loadingOlder, hasMoreMessages, fetchMessages]);

  const closeConversation = useCallback(() => {
    setActiveContact(null);
    setMessages([]);
    setHasMoreMessages(false);
    setReplyTo(null);
    setPendingNewCount(0);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    fetchUnread();
    sendHeartbeat();
    const id = setInterval(() => {
      fetchUnread();
      sendHeartbeat();
    }, UNREAD_POLL_MS);
    return () => clearInterval(id);
  }, [enabled, fetchUnread, sendHeartbeat]);

  useEffect(() => {
    if (!enabled || !panelOpen) return undefined;
    fetchEmployees();
    const id = setInterval(() => fetchEmployees(), EMPLOYEES_POLL_MS);
    return () => clearInterval(id);
  }, [enabled, panelOpen, fetchEmployees]);

  useEffect(() => {
    if (!enabled || !panelOpen) return undefined;
    const t = setTimeout(() => fetchEmployees(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery, enabled, panelOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const conversationId = activeContact?.conversation_id;
    if (!enabled || !panelOpen || !conversationId) return undefined;

    const poll = async () => {
      try {
        const res = await api.get(`/crm/chat/conversations/${conversationId}/messages`, {
          params: { limit: 50 },
        });
        const incoming = res.data?.data?.messages || [];
        const last = incoming[incoming.length - 1];
        const prevLast = lastMessageIdsRef.current[conversationId];

        if (last?.id && prevLast && last.id > prevLast) {
          const newIncoming = incoming.filter((m) => m.id > prevLast && !m.is_mine);
          if (newIncoming.length > 0) {
            await markRead(conversationId);
          }
        }

        const prev = messagesRef.current;
        const newCount = countNewIncoming(prev, incoming);
        const oldestPrev = prev[0]?.id;
        const oldestIncoming = incoming[0]?.id;
        // Only trust poll has_more when client still on latest window (no older pages loaded)
        const keptOlderHistory =
          oldestPrev != null && oldestIncoming != null && oldestPrev < oldestIncoming;

        if (newCount > 0 && !isNearBottomRef.current && !userJustSentRef.current) {
          setPendingNewCount((c) => c + newCount);
        }

        if (!keptOlderHistory) {
          const hasMore = res.data?.data?.has_more ?? false;
          setHasMoreMessages(hasMore);
          hasMoreMessagesRef.current = hasMore;
        }

        setMessages(mergeMessagesById(prev, incoming));

        if (last?.id) {
          lastMessageIdsRef.current[conversationId] = last.id;
        }
      } catch {
        // ignore
      }
    };

    poll();
    const id = setInterval(poll, MESSAGES_POLL_MS);
    return () => clearInterval(id);
  }, [enabled, panelOpen, activeContact?.conversation_id, markRead]);

  useEffect(() => {
    if (!enabled || panelOpen) return;
    if (unreadTotal > prevUnreadRef.current && prevUnreadRef.current >= 0) {
      toast({
        title: 'New CRM message',
        description: 'You have unread messages in CRM Chat.',
      });
      playNotificationSound();
    }
    prevUnreadRef.current = unreadTotal;
  }, [unreadTotal, panelOpen, enabled, toast]);

  useEffect(() => {
    if (!activeUserId || !employees.length) return;
    const found = employees.find((e) => Number(e.user_id) === Number(activeUserId));
    if (found && !activeContact) {
      openConversationWith(found);
    }
  }, [activeUserId, employees]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    employees,
    loadingEmployees,
    messages,
    loadingMessages,
    loadingOlder,
    hasMoreMessages,
    unreadTotal,
    activeContact,
    searchQuery,
    setSearchQuery,
    replyTo,
    uploadProgress,
    highlightedMessageId,
    pendingNewCount,
    listContainerRef,
    fetchEmployees,
    fetchUnread,
    openConversationWith,
    sendMessage,
    sendAttachment,
    loadOlderMessages,
    closeConversation,
    markRead,
    cancelReply,
    setReplyTarget,
    scrollToMessage,
    registerMessageRef,
    setNearBottom,
    clearPendingNew,
    consumeJustSent,
    formatChatTime,
    formatLastSeen,
    getCrmChatAssetUrl,
  };
}
